/**
 * Promotions: "20% off all dresses", "buy 2 get 1 free".
 *
 * Made here, applied by every till on its own - offline too - and shown to the
 * customer on the screen and the receipt. One rule shapes this screen: once a
 * promotion has started it can only be STOPPED, never changed, because sales
 * were made under it. Before it starts it can be changed or removed freely.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { promoProblem, promoRunning, promoSummary, type Promotion } from '@pos/shared';
import { useI18n, useT } from '../../core/i18n/index.js';
import type { StringKey } from '../../core/i18n/en.js';
import { useSettings } from '../../core/state/settings.js';
import { useToast } from '../../core/state/toast.js';
import { useSync } from '../../core/sync.js';
import { api, type ApiError } from '../../core/api.js';
import { Button, Confirm, Empty, Field, Loading, Modal, Pill, Tabs } from '../../core/ui/ui.js';
import { useRefs } from './Products.js';

interface Row extends Promotion {
  createdAt: string;
  stoppedAt: string | null;
  sales: number;
  pieces: number;
  given: number;
}

type Status = 'running' | 'soon' | 'over';

function statusOf(p: Promotion, now = Date.now()): Status {
  if (promoRunning(p, now)) return 'running';
  return new Date(p.startsAt).getTime() > now ? 'soon' : 'over';
}

/** For <input type="datetime-local">: local time, to the minute. */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function Promotions() {
  const t = useT();
  const { lang } = useI18n();
  const { money, dateTime } = useSettings();
  const toast = useToast();
  const { refreshCatalog } = useSync();
  const refs = useRefs();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [tab, setTab] = useState<'current' | 'over'>('current');
  const [editing, setEditing] = useState<Row | 'new' | null>(null);
  const [stopping, setStopping] = useState<Row | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await api<Row[]>('/catalog/promotions'));
    } catch {
      setRows([]);
      toast.err(t('net.needOnline'));
    }
  }, [t, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const name = (p: { nameEn: string; nameAr: string }) => (lang === 'ar' ? p.nameAr || p.nameEn : p.nameEn || p.nameAr);
  const refName = (list: { id: string; name_en: string; name_ar: string }[], id: string) => {
    const r = list.find((x) => x.id === id);
    return r ? (lang === 'ar' ? r.name_ar || r.name_en : r.name_en || r.name_ar) : '…';
  };
  const scopeText = (p: Promotion) => {
    if (p.scope === 'all') return t('promo.scopeAll');
    if (p.scope === 'categories') return p.targetIds.map((id) => refName(refs.categories, id)).join('، ');
    if (p.scope === 'brands') return p.targetIds.map((id) => refName(refs.brands, id)).join('، ');
    return t('promo.nProducts', { n: p.targetIds.length });
  };

  const stop = async (p: Row) => {
    setStopping(null);
    try {
      await api(`/catalog/promotions/${p.id}/stop`, { body: {} });
      toast.ok(statusOf(p) === 'soon' ? t('promo.removed') : t('promo.stoppedOk'));
      await load();
      void refreshCatalog().catch(() => undefined);
    } catch (e) {
      toast.err((e as ApiError).status === 0 ? t('net.needOnline') : (e as Error).message);
    }
  };

  const shown = useMemo(() => {
    if (!rows) return [];
    const now = Date.now();
    return rows.filter((r) => (tab === 'over' ? statusOf(r, now) === 'over' : statusOf(r, now) !== 'over'));
  }, [rows, tab]);

  if (!rows) return <Loading />;
  const overCount = rows.filter((r) => statusOf(r) === 'over').length;

  return (
    <div className="col">
      <div className="row wrap">
        <p className="muted" style={{ flex: '1 1 320px', margin: 0 }}>{t('promo.intro')}</p>
        <Button variant="primary" onClick={() => setEditing('new')}>{t('promo.new')}</Button>
      </div>

      <Tabs
        tabs={[
          { key: 'current', label: t('promo.tabCurrent'), count: rows.length - overCount },
          { key: 'over', label: t('promo.tabOver'), count: overCount },
        ]}
        active={tab}
        onChange={setTab}
      />

      {shown.length === 0 ? (
        <Empty
          icon="🏷"
          title={tab === 'over' ? t('promo.noneOver') : t('promo.none')}
          hint={tab === 'over' ? undefined : t('promo.noneHint')}
          action={tab === 'over' ? undefined : <Button variant="primary" onClick={() => setEditing('new')}>{t('promo.new')}</Button>}
        />
      ) : (
        <div className="promo-list">
          {shown.map((p) => {
            const st = statusOf(p);
            return (
              <div key={p.id} className={`card promo-card ${st}`}>
                <div className="promo-head">
                  <div style={{ minWidth: 0 }}>
                    <div className="strong">{name(p)}</div>
                    <div className="promo-what">{promoSummary(p, lang === 'ar' ? 'ar' : 'en')}</div>
                  </div>
                  <Pill kind={st === 'running' ? 'ok' : st === 'soon' ? 'info' : undefined}>
                    {t(`promo.status.${st}` as StringKey)}
                  </Pill>
                </div>
                <div className="small muted">{t('promo.appliesTo')} {scopeText(p)}</div>
                <div className="small muted">
                  {dateTime(p.startsAt)} → {p.endsAt ? dateTime(p.endsAt) : t('promo.untilStopped')}
                </div>
                {st !== 'soon' ? (
                  <div className="promo-stats">
                    <span>{t('promo.statSales', { n: p.sales })}</span>
                    <span>{t('promo.statPieces', { n: p.pieces })}</span>
                    <span>{t('promo.statGiven', { amount: money(p.given) })}</span>
                  </div>
                ) : null}
                {st !== 'over' ? (
                  <div className="row wrap" style={{ gap: 'var(--s-2)' }}>
                    {st === 'soon' ? <Button size="sm" onClick={() => setEditing(p)}>{t('common.edit')}</Button> : null}
                    <Button size="sm" variant={st === 'running' ? 'danger' : 'ghost'} onClick={() => setStopping(p)}>
                      {st === 'running' ? t('promo.stop') : t('promo.remove')}
                    </Button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {editing ? (
        <PromoEditor
          promo={editing === 'new' ? null : editing}
          refs={refs}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
            void refreshCatalog().catch(() => undefined);
          }}
        />
      ) : null}

      {stopping ? (
        <Confirm
          title={statusOf(stopping) === 'running' ? t('promo.stopTitle') : t('promo.removeTitle')}
          message={statusOf(stopping) === 'running' ? t('promo.stopMsg') : t('promo.removeMsg')}
          confirmLabel={statusOf(stopping) === 'running' ? t('promo.stop') : t('promo.remove')}
          danger
          onConfirm={() => void stop(stopping)}
          onCancel={() => setStopping(null)}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Making or changing one
// ---------------------------------------------------------------------------

type Draft = Omit<Promotion, 'id'>;

function PromoEditor({
  promo,
  refs,
  onClose,
  onSaved,
}: {
  promo: Promotion | null;
  refs: ReturnType<typeof useRefs>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const { lang } = useI18n();
  const toast = useToast();
  const [d, setD] = useState<Draft>(
    promo ?? {
      nameEn: '',
      nameAr: '',
      kind: 'percent',
      percent: 20,
      buyQty: 2,
      getQty: 1,
      scope: 'all',
      targetIds: [],
      startsAt: new Date().toISOString(),
      endsAt: null,
    },
  );
  const [search, setSearch] = useState('');
  const [products, setProducts] = useState<{ id: string; name_en: string; name_ar: string; sku: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const set = (patch: Partial<Draft>) => setD((x) => ({ ...x, ...patch }));

  useEffect(() => {
    if (d.scope !== 'products') return;
    const timer = setTimeout(() => {
      void api<{ id: string; name_en: string; name_ar: string; sku: string }[]>(
        `/catalog/products?q=${encodeURIComponent(search)}`,
      )
        .then(setProducts)
        .catch(() => undefined);
    }, 200);
    return () => clearTimeout(timer);
  }, [d.scope, search]);

  const nm = (r: { name_en: string; name_ar: string }) => (lang === 'ar' ? r.name_ar || r.name_en : r.name_en || r.name_ar);
  const choices =
    d.scope === 'categories' ? refs.categories : d.scope === 'brands' ? refs.brands : d.scope === 'products' ? products : [];
  const toggle = (id: string) =>
    set({ targetIds: d.targetIds.includes(id) ? d.targetIds.filter((x) => x !== id) : [...d.targetIds, id] });

  const save = async () => {
    const why = promoProblem(d);
    if (why) {
      setProblem(t(`promo.err.${why}` as StringKey));
      return;
    }
    setBusy(true);
    setProblem('');
    try {
      await api(promo ? `/catalog/promotions/${promo.id}` : '/catalog/promotions', {
        method: promo ? 'PUT' : 'POST',
        body: d,
      });
      toast.ok(new Date(d.startsAt).getTime() <= Date.now() ? t('promo.savedRunning') : t('promo.savedSoon'));
      onSaved();
    } catch (e) {
      const x = e as ApiError;
      if (x.status === 0) setProblem(t('net.needOnline'));
      else if (x.code?.startsWith('promo_')) {
        const key = `promo.err.${x.code.slice(6)}`;
        setProblem(t(key as StringKey) !== key ? t(key as StringKey) : x.message);
      } else setProblem(x.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={promo ? t('promo.editTitle') : t('promo.new')}
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" busy={busy} onClick={() => void save()}>{t('common.save')}</Button>
        </>
      }
    >
      <div className="col">
        <div className="grid c2">
          <Field label={t('promo.nameAr')}>
            <input value={d.nameAr} dir="rtl" onChange={(e) => set({ nameAr: e.target.value })} placeholder="تخفيضات الصيف" />
          </Field>
          <Field label={t('promo.nameEn')}>
            <input value={d.nameEn} dir="ltr" onChange={(e) => set({ nameEn: e.target.value })} placeholder="Summer sale" />
          </Field>
        </div>

        <Field label={t('promo.kind')}>
          <div className="btn-row">
            <Button variant={d.kind === 'percent' ? 'primary' : 'default'} onClick={() => set({ kind: 'percent', percent: d.percent === 100 ? 20 : d.percent })}>
              {t('promo.kindPercent')}
            </Button>
            <Button variant={d.kind === 'bxgy' ? 'primary' : 'default'} onClick={() => set({ kind: 'bxgy', percent: 100, buyQty: d.buyQty || 2, getQty: d.getQty || 1 })}>
              {t('promo.kindBxgy')}
            </Button>
          </div>
        </Field>

        {d.kind === 'percent' ? (
          <Field label={t('promo.percentOff')}>
            <div className="row" style={{ gap: 'var(--s-2)' }}>
              <input type="number" min={1} max={99} inputMode="numeric" value={d.percent} style={{ width: 110 }}
                onChange={(e) => set({ percent: Math.round(Number(e.target.value)) })} />
              <span>%</span>
            </div>
          </Field>
        ) : (
          <div className="promo-bxgy">
            <Field label={t('promo.buy')}>
              <input type="number" min={1} max={20} inputMode="numeric" value={d.buyQty} onChange={(e) => set({ buyQty: Math.round(Number(e.target.value)) })} />
            </Field>
            <Field label={t('promo.get')}>
              <input type="number" min={1} max={20} inputMode="numeric" value={d.getQty} onChange={(e) => set({ getQty: Math.round(Number(e.target.value)) })} />
            </Field>
            <Field label={t('promo.at')}>
              <div className="btn-row">
                <Button variant={d.percent === 100 ? 'primary' : 'default'} onClick={() => set({ percent: 100 })}>{t('promo.free')}</Button>
                <Button variant={d.percent === 50 ? 'primary' : 'default'} onClick={() => set({ percent: 50 })}>{t('promo.half')}</Button>
                <input type="number" min={1} max={100} inputMode="numeric" value={d.percent} style={{ width: 90 }}
                  aria-label={t('promo.percentOff')} onChange={(e) => set({ percent: Math.round(Number(e.target.value)) })} />
                <span>%</span>
              </div>
            </Field>
          </div>
        )}

        <Field label={t('promo.appliesTo')}>
          <div className="btn-row">
            {(['all', 'categories', 'brands', 'products'] as const).map((s) => (
              <Button key={s} variant={d.scope === s ? 'primary' : 'default'} onClick={() => set({ scope: s, targetIds: s === d.scope ? d.targetIds : [] })}>
                {t(`promo.scope.${s}` as StringKey)}
              </Button>
            ))}
          </div>
        </Field>

        {d.scope !== 'all' ? (
          <div className="promo-targets">
            {d.scope === 'products' ? (
              <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('common.searchPlaceholder')} />
            ) : null}
            {choices.length === 0 ? (
              <p className="small muted">{t('promo.nothingToPick')}</p>
            ) : (
              <div className="promo-checks">
                {choices.map((c) => (
                  <label key={c.id} className="promo-check">
                    <input type="checkbox" checked={d.targetIds.includes(c.id)} onChange={() => toggle(c.id)} />
                    <span>{nm(c)}</span>
                  </label>
                ))}
              </div>
            )}
            <div className="xsmall muted">{t('promo.chosen', { n: d.targetIds.length })}</div>
          </div>
        ) : null}

        <div className="grid c2">
          <Field label={t('promo.starts')}>
            <input type="datetime-local" value={toLocalInput(d.startsAt)}
              onChange={(e) => e.target.value && set({ startsAt: new Date(e.target.value).toISOString() })} />
          </Field>
          <Field label={t('promo.ends')} hint={t('promo.endsHint')}>
            <input type="datetime-local" value={d.endsAt ? toLocalInput(d.endsAt) : ''}
              onChange={(e) => set({ endsAt: e.target.value ? new Date(e.target.value).toISOString() : null })} />
          </Field>
        </div>

        <div className="promo-preview">
          <span aria-hidden>🏷</span> {promoSummary(d, lang === 'ar' ? 'ar' : 'en')}
        </div>
        <p className="xsmall muted" style={{ margin: 0 }}>{t('promo.rules')}</p>
        {problem ? <p role="alert" style={{ color: 'var(--danger)', margin: 0 }}>{problem}</p> : null}
      </div>
    </Modal>
  );
}
